import ApiService from './';
import { FormSettingsParams } from '../params/redux/form';
/**
 * @heading1: Folder API service.
 * @paragraph: This module provides a set of methods to interact with the Folder API.
 * It handles operations like Fetching folder details and create folder.
 */
const FormApi = {
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    createUserForm: (token: string, data: Record<string, unknown>) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'create-user-form',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    fetchForms: (token: string, client_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'get-user-form',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    editForm: (token: string, data: Record<string, unknown>) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'edit-form',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    deleteForm: (token: string, form_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'delete-form',
            method: 'post',
            token,
            data: { form_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    duplicateForm: (token: string, form_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'duplicate-form',
            method: 'post',
            token,
            data: { form_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    assignForm: (token: string, form_id: number, user_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'add-form-assignee',
            method: 'post',
            token,
            data: { form_id, user_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    removeAssignee: (token: string, form_id: number, user_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'remove-form-assignee',
            method: 'post',
            token,
            data: { form_id, user_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    formSettings: (token: string, data: FormSettingsParams) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'form-settings',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    saveTemplateForm: (token: string, data: Record<string, unknown>) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'save-as-template',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    changeFormStatus: (token: string, data: Record<string, unknown>) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'change-form-status',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    createDefaultForms: (token: string, client_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'create-default-form',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchFacebookPages: (token: string, client_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'get-facebook-pages',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    disconnetFacebookPage: (token: string, client_id: number, page_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'disconnect-page',
            method: 'post',
            token,
            data: { client_id, page_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    updateDefaultForm: (token: string, forms_id: number[], pipeline_id: number, stage_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'update-default-form',
            method: 'post',
            token,
            data: { forms_id, pipeline_id, stage_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    refetchAllFacebookPages: (token: string, client_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'refresh-facebook-pages',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    previewRenderForm: (token: string, form_json: string, form_title: string, template_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'preview-render-form',
            method: 'post',
            token,
            data: { form_json, form_title, template_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchFormTemplates: (token: string) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'get-form-templates',
            method: 'post',
            token,
            data: {}
        });
    },
    connectExternalForm: (token: string, data: {
        client_id: number;
        type: 'snapps-manual' | 'elementor-manual';
        form_name: string;
        pipeline_id: number;
        stage_id: number;
    }) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'create-external-form',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default FormApi;
